import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Search, FolderKanban, Calendar, LayoutGrid, List, MapPin, Users, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { format, parseISO } from 'date-fns';
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '@/contexts/AuthContext';
import { Badge } from '@/components/ui/badge';
import { projectService } from '@/services';
import { profileService } from '@/services';
import ProjectFormDialog from '@/components/project/ProjectFormDialog';

interface ProjectWithRelations {
  id: string;
  name: string;
  description: string | null;
  region_id: string | null;
  project_manager: string | null;
  expected_start_date: string | null;
  expected_end_date: string | null;
  list_regions: { display_value: string } | null;
  manager_profile: { full_name: string | null } | null;
  project_segments: { count: number }[];
}

export default function Projects() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [searchQuery, setSearchQuery] = useState('');
  const [viewMode, setViewMode] = useState<'cards' | 'table'>('cards');
  const [dialogOpen, setDialogOpen] = useState(false);

  const { data: profile } = useQuery({
    queryKey: ['profile', user?.id],
    queryFn: async () => {
      if (!user?.id) return null;
      const result = await profileService.get(user.id);
      if (result.error) throw result.error;
      return result.data;
    },
    enabled: !!user?.id,
  });

  const { data: projects = [], isLoading } = useQuery({
    queryKey: ['projects', profile?.company_id],
    queryFn: async () => {
      if (!profile?.company_id) return [];
      const result = await projectService.listWithRelations(profile.company_id);
      if (result.error) throw result.error;
      return result.data as ProjectWithRelations[];
    },
    enabled: !!profile?.company_id,
  });

  const filteredProjects = projects.filter(project => {
    const q = searchQuery.toLowerCase();
    return project.name.toLowerCase().includes(q) ||
      (project.description || '').toLowerCase().includes(q);
  });

  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return '—';
    return format(parseISO(dateStr), 'MMM d, yyyy');
  };

  const getSegmentCount = (project: ProjectWithRelations) => {
    return project.project_segments?.[0]?.count ?? 0;
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <>
    <div className="animate-slide-up">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-8">
        <div>
          <h1 className="page-title">Projects</h1>
          <p className="page-subtitle">Manage and track your projects across regions and segments.</p>
        </div>
        <button className="btn-primary" onClick={() => setDialogOpen(true)}>
          <Plus className="w-5 h-5" />
          New Project
        </button>
      </div>

      {/* Search and View Toggle */}
      <div className="flex flex-col md:flex-row gap-4 mb-6 items-start md:items-center justify-between">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
          <input
            type="text"
            placeholder="Search projects..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="input-field pl-12"
          />
        </div>
        <ToggleGroup
          type="single"
          value={viewMode}
          onValueChange={(value) => value && setViewMode(value as 'cards' | 'table')}
          className="bg-muted rounded-lg p-1"
        >
          <ToggleGroupItem value="cards" aria-label="Card view" className="px-3 py-1.5 data-[state=on]:bg-background data-[state=on]:shadow-sm">
            <LayoutGrid className="w-4 h-4" />
          </ToggleGroupItem>
          <ToggleGroupItem value="table" aria-label="Table view" className="px-3 py-1.5 data-[state=on]:bg-background data-[state=on]:shadow-sm">
            <List className="w-4 h-4" />
          </ToggleGroupItem>
        </ToggleGroup>
      </div>

      {/* Card View */}
      {viewMode === 'cards' && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredProjects.map((project) => (
            <div key={project.id} className="stat-card cursor-pointer group" onClick={() => navigate(`/projects/${project.id}`)}>
              <div className="flex items-start justify-between mb-4">
                <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center group-hover:bg-primary group-hover:text-primary-foreground transition-colors">
                  <FolderKanban className="w-6 h-6 text-primary group-hover:text-primary-foreground" />
                </div>
                <Badge variant="secondary">
                  {getSegmentCount(project)} segment{getSegmentCount(project) !== 1 ? 's' : ''}
                </Badge>
              </div>
              <h3 className="font-semibold text-foreground mb-1">{project.name}</h3>
              <div className="flex items-center gap-2 text-sm text-muted-foreground mb-3">
                {project.list_regions && (
                  <>
                    <MapPin className="w-4 h-4" />
                    <span>{project.list_regions.display_value}</span>
                  </>
                )}
                {project.manager_profile?.full_name && (
                  <>
                    {project.list_regions && <span>•</span>}
                    <Users className="w-4 h-4" />
                    <span>{project.manager_profile.full_name}</span>
                  </>
                )}
              </div>
              {!project.list_regions && !project.manager_profile?.full_name && <div className="mb-3" />}
              <div className="flex flex-col gap-1 text-sm text-muted-foreground border-t border-border pt-3">
                <div className="flex items-center justify-between">
                  <span>Start:</span>
                  <span>{formatDate(project.expected_start_date)}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span>End:</span>
                  <span>{formatDate(project.expected_end_date)}</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Table View */}
      {viewMode === 'table' && (
        <div className="bg-card border border-border rounded-lg overflow-hidden">
          <table className="data-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Region</th>
                <th>Start Date</th>
                <th>End Date</th>
                <th>Segments</th>
                <th>Manager</th>
              </tr>
            </thead>
            <tbody>
              {filteredProjects.map((project) => (
                <tr key={project.id} className="cursor-pointer" onClick={() => navigate(`/projects/${project.id}`)}>
                  <td>
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                        <FolderKanban className="w-5 h-5 text-primary" />
                      </div>
                      <div>
                        <span className="font-medium">{project.name}</span>
                        {project.description && (
                          <p className="text-sm text-muted-foreground line-clamp-1">{project.description}</p>
                        )}
                      </div>
                    </div>
                  </td>
                  <td className="text-muted-foreground">
                    {project.list_regions?.display_value || '—'}
                  </td>
                  <td>
                    <div className="flex items-center gap-2 text-muted-foreground">
                      <Calendar className="w-4 h-4" />
                      {formatDate(project.expected_start_date)}
                    </div>
                  </td>
                  <td className="text-muted-foreground">{formatDate(project.expected_end_date)}</td>
                  <td className="text-muted-foreground">{getSegmentCount(project)}</td>
                  <td className="text-muted-foreground">{project.manager_profile?.full_name || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {filteredProjects.length === 0 && !isLoading && (
        <div className="text-center py-12 text-muted-foreground">
          {projects.length === 0
            ? 'No projects yet. Create your first project to get started.'
            : 'No projects found matching your search.'}
        </div>
      )}
    </div>

      <ProjectFormDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        onSave={async (data) => {
          if (!profile?.company_id || !user?.id) return;
          const result = await projectService.create(
            {
              name: data.name,
              description: data.description || null,
              region_id: data.region_id || null,
              expected_start_date: data.expected_start_date || null,
              expected_end_date: data.expected_end_date || null,
              project_manager: data.project_manager || null,
            },
            { companyId: profile.company_id, userId: user.id }
          );
          if (result.error) {
            toast.error('Failed to create project');
            throw result.error;
          }
          toast.success('Project created');
          if (result.data) {
            navigate(`/projects/${result.data.id}`);
          }
        }}
      />
    </>
  );
}
